import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, Observable, Subscription } from 'rxjs';
import { LoadingComponent } from '../models/loading.component';
import {ScenarioImageService} from "./scenario-image/scenario-image.service";

export type RefineMode = 'PREVIEW' | 'EDIT'
  // Needed to show refinement ui elements for a few seconds after the image upload
  | 'PREVIEW_WITH_TEMP_CORNERS';

@Injectable({providedIn: 'root'})
export class ApplicationStateService {

  constructor(private readonly scenarioImageService: ScenarioImageService) {
  }

  protected isApplicationLoaded$ = new BehaviorSubject(false);
  protected loadingComponents: Array<LoadingComponent> = [];
  protected componentsSub: Subscription = new Subscription();
  protected refineMode$ = new BehaviorSubject<RefineMode>('PREVIEW');
  protected showFilter$ = new BehaviorSubject<boolean>(false);

  protected hasUploadedImage$ = new BehaviorSubject<boolean>(true);

  isApplicationLoaded(): Observable<boolean> {
    return this.isApplicationLoaded$.asObservable().pipe(distinctUntilChanged());
  }

  register(loadingComponent: LoadingComponent) {
    this.loadingComponents.push(loadingComponent);

    this.resubscribe();
  }

  unregister(loadingComponent: LoadingComponent) {
    let index = this.loadingComponents.indexOf(loadingComponent);
    if(index !== -1) {
      this.loadingComponents.splice(index, 1);
    }

    this.resubscribe();
  }

  setRefineMode(refineMode: RefineMode): void {
    if (refineMode === 'EDIT') {
      this.scenarioImageService.stopPollingForVisualisationImprovements();
    }

    this.refineMode$.next(refineMode);
  }

  getRefineMode(): Observable<RefineMode> {
    return this.refineMode$.asObservable();
  }

  setShowFilter(showFilter: boolean): void {
    this.showFilter$.next(showFilter);
  }

  getShowFilter(): Observable<boolean> {
    return this.showFilter$.asObservable();
  }

  setHasUploadedImage(hasUploadedImage: boolean): void {
    this.hasUploadedImage$.next(hasUploadedImage);
  }

  getHasUploadedImage(): Observable<boolean> {
    return this.hasUploadedImage$.asObservable();
  }

  protected resubscribe(): void {
    const newSub = combineLatest(this.loadingComponents.map(c => c.isLoaded())).subscribe(result => {
      this.isApplicationLoaded$.next(result.every(b => b === true));
    });
    this.componentsSub.unsubscribe();
    this.componentsSub = newSub;
  }

}
